import { type ApolloQueryResult, type FetchResult } from '@apollo/client';

import { BaseError, type BaseErrorOptions } from '@canvas/error';
export class GraphQLClientError extends BaseError {
  constructor(message: string, options?: BaseErrorOptions) {
    super({ name: 'GraphQLClientError', ...options }, message);
  }
}

/**
 * Throws an error if the response contains errors or is empty
 * and will throw the custom error if provided.
 *
 * @param responseOrErr
 * @param customError
 */
export function throwOnQueryError<T>(
  responseOrErr: ApolloQueryResult<T> | Error,
  customError?: GraphQLClientError
): asserts responseOrErr is ApolloQueryResult<T> & { data: T } {
  if (responseOrErr instanceof Error) {
    if (customError && responseOrErr instanceof GraphQLClientError) {
      throw customError;
    }

    throw responseOrErr;
  }

  if (responseOrErr.errors && responseOrErr.errors.length > 0) {
    throw new GraphQLClientError(
      `graphql response contained errors: ${JSON.stringify(responseOrErr.errors)}`
    );
  }

  if (!responseOrErr.data) {
    throw new GraphQLClientError(
      `graphql empty response, errors: ${JSON.stringify(responseOrErr.errors)}`
    );
  }
}

/**
 * Throws an error if the response contains errors or is empty
 * and will throw the custom error if provided.
 *
 * @param responseOrErr
 * @param customError
 */
export function throwOnMutationError<T>(
  responseOrErr: FetchResult<T> | Error,
  customError?: GraphQLClientError
): asserts responseOrErr is FetchResult<T> & { data: T } {
  if (responseOrErr instanceof Error) {
    if (customError && responseOrErr instanceof GraphQLClientError) {
      throw customError;
    }

    throw responseOrErr;
  }

  if (responseOrErr.errors && responseOrErr.errors.length > 0) {
    throw new GraphQLClientError(
      `graphql response contained errors: ${JSON.stringify(responseOrErr.errors)}`
    );
  }

  if (!responseOrErr.data) {
    throw new GraphQLClientError(
      `graphql empty response, errors: ${JSON.stringify(responseOrErr.errors)}`
    );
  }
}
